import { useEffect, useRef } from 'react';
import * as THREE from 'three';

// eslint-disable-next-line import/order
import { TilesTextureProps } from '../../@types/ContextProviderRelated';

// eslint-disable-next-line import/named
import { ENLARGEMENT_FACTOR, PERCENTAGE, UNIT_TO_METER_DIVISOR } from '../../utils/constants';

const TilesTexture = ({
    width,
    length,
    jointColor,
    tileColor,
    jointWidth,
    rotation,
    onTextureUpdate
}: TilesTextureProps) => {
    const canvasRef = useRef<HTMLCanvasElement>();

    useEffect(() => {
    // Initialize the canvas if it doesn't exist
        if (!canvasRef.current) {
            canvasRef.current = document.createElement('canvas');
        }

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Adjust canvas size based on input width and length
        // Scale up the canvas for smaller dimensions to enhance detail
        const scaleFactor = width <= PERCENTAGE && length <= PERCENTAGE ? ENLARGEMENT_FACTOR : 1;
        const adjustedWidth = width * scaleFactor;
        const adjustedLength = length * scaleFactor;
        const adjustedJointWidth = jointWidth * scaleFactor;

        if (canvas.width !== adjustedWidth || canvas.height !== adjustedLength) {
            canvas.width = adjustedWidth;
            canvas.height = adjustedLength;
        }
        if(ctx !== null) {
            // Clear the canvas
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Draw the background (joint area)
            ctx.fillStyle = jointColor;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Draw the tile area
            ctx.fillStyle = tileColor;
            ctx.fillRect(
                adjustedJointWidth,
                adjustedJointWidth,
                canvas.width - 2 * adjustedJointWidth,
                canvas.height - 2 * adjustedJointWidth
            );
        }

        // Create a THREE texture from the canvas
        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(UNIT_TO_METER_DIVISOR / width, UNIT_TO_METER_DIVISOR / length);
        texture.rotation = rotation;

        // Call the callback function to update the texture in the parent component
        onTextureUpdate(texture);

        // Clean up the texture when the component unmounts or when the effect re-runs
        return () => {
            texture.dispose();
        };
    }, [width, length, jointColor, jointWidth, tileColor, rotation, onTextureUpdate]);

    return null;
};

export default TilesTexture;
